/**
 * Maps the `GET /api/courses/:id/builder` payload into the three step-data
 * shapes the course-builder stepper consumes.
 *
 * Kept out of CourseBuilderForm so it is reachable from a test without
 * mounting the whole stepper. The output shapes are load-bearing: TopicsStep,
 * CourseInformationStep and AssessmentBuilderStep read these exact field
 * names, so changing one here means changing it there too.
 */

const cdnUrl = (fileUpload) =>
  fileUpload ? import.meta.env.VITE_CDN_LINK + fileUpload.s3Key : null;

const mapMaterialContent = (material) => ({
  file: {},
  fileSize: 0,
  fileType: material?.fileUpload?.fileType,
  url: cdnUrl(material?.fileUpload),
  filename: material?.fileUpload?.filename,
  fileCategory: "LEARNING_MATERIAL",
});

const mapQuestion = (question) => ({
  id: question.id,
  correctAnswer: question.correctAnswer,
  question: question.question,
  type: question.type,
  options: question.options,
});

const mapMaterialTest = (test) => ({
  id: test.id,
  title: test.title,
  type: test.type,
  timeLimit: test.timeLimit,
  questions: test.questions ?? [],
  questionSets: (test.questionSets ?? []).map((set) => ({
    id: set.id,
    title: set.name,
    questions: (set.questions ?? []).map(mapQuestion),
  })),
});

const mapMaterial = (material) => ({
  id: material.id,
  title: material.title,
  displayOrder: material.displayOrder,
  drmEnabled: material.drmEnabled,
  allowDownload: material.allowDownload,
  // LearningMaterial has no `duration` column, so this is undefined — kept
  // verbatim from the previous implementation rather than silently changed.
  duration: material.duration,
  content: mapMaterialContent(material),
  // Always the `test` key holding an array, so consumers have one shape to
  // read. The old code emitted `test: [one]` on success but `tests: []` when
  // there was no test; nothing ever read that second key.
  test: material.test ? [mapMaterialTest(material.test)] : [],
});

const mapAssessment = (assessment) => ({
  id: assessment.id,
  title: assessment.title,
  description: assessment.description,
  type: assessment.type,
  difficulty: assessment.difficulty,
  allowRetakes: true,
  enableRandomization: true,
  gradingType: "AUTOMATIC",
  maxAttempts: assessment.maxAttempts,
  passingScore: assessment.passingScore,
  showCorrectAnswers: assessment.showCorrectAnswers,
  questionCount: assessment.questionsPerTest,
  questionSets: assessment.questionSets ?? [],
  questions: assessment.questions ?? [],
});

/**
 * @param {object} data - the `data` field of the builder response envelope
 * @returns {{courseInfo: object, topics: object[], assessment: object[]}}
 */
export function buildStepDataFromBuilderResponse(data) {
  const { topics = [], courseAssessment, ...course } = data ?? {};

  return {
    courseInfo: {
      title: course.name,
      description: course.description,
      coverImage: course.coverImage,
      tags: course.tags,
    },
    topics: topics.map((topic) => ({
      id: topic.id,
      name: topic.name,
      description: topic.description,
      displayOrder: topic.displayOrder,
      estimatedDuration: topic.estimatedDuration,
      learningMaterials: (topic.learningMaterials ?? []).map(mapMaterial),
    })),
    // courseAssessment is a single object or null (Test.courseId is @unique),
    // where the old searchTests call returned an array.
    assessment: courseAssessment ? [mapAssessment(courseAssessment)] : [],
  };
}
